import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LockKey, Warning, EyeSlash } from "@phosphor-icons/react";
import PlausibleFunnelWidget from "@/pages/admin/PlausibleFunnelWidget";
import PlausibleEngagementWidget from "@/pages/admin/PlausibleEngagementWidget";

// Admin dashboard — intentionally NOT linked from anywhere in the UI.
// Access via /admin only, protected by ADMIN_TOKEN header. Password is
// kept in-memory only (never persisted to localStorage) so refreshing the
// page requires re-entering it.
export default function Admin() {
  const [token, setToken] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const reload = async (tk) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const res = await axios.get(`${base}/api/admin/stats`, {
      headers: { "X-Admin-Token": tk },
    });
    setData(res.data);
    setLastRefreshed(new Date());
  };

  // Silent auto-refresh every 30s while the dashboard is open. Prevents the
  // "stale UI" trap where the operator sees a frozen counter from hours ago.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  useEffect(() => {
    if (!data) return; // not logged in yet
    const id = setInterval(() => {
      reload(tokenRef.current).catch(() => { /* keep last data on transient error */ });
    }, 30000);
    return () => clearInterval(id);
  }, [data]);

  const login = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await reload(token);
    } catch (err) {
      setError(err.response?.status === 404
        ? "Token non valido."
        : "Errore imprevisto.");
    } finally {
      setLoading(false);
    }
  };

  const cleanupTest = async () => {
    if (!window.confirm("Rimuovere TUTTI gli account di test dal database? Questa azione è irreversibile.")) return;
    setCleaning(true);
    setCleanupResult(null);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.delete(`${base}/api/admin/test-accounts`, {
        headers: { "X-Admin-Token": token },
      });
      setCleanupResult(res.data);
      await reload(token);
    } catch {
      alert("Errore durante la rimozione.");
    } finally {
      setCleaning(false);
    }
  };

  // Toggle a single user's `is_internal_tester` flag. Optimistically flips
  // the row so the UI responds instantly, then reconciles with the server.
  const toggleInternal = async (userId, next) => {
    if (!userId) return;
    setData(d => ({
      ...d,
      users: d.users.map(u => u.user_id === userId ? { ...u, is_internal_tester: next } : u),
    }));
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      await axios.patch(
        `${base}/api/admin/users/${userId}/internal-tester`,
        { value: next },
        { headers: { "X-Admin-Token": token } },
      );
      await reload(token);
    } catch {
      // revert optimistic update on failure
      await reload(token).catch(() => {});
      alert("Errore durante il cambio flag.");
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 py-10 bg-surface">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-3xl border border-border bg-surface-card p-8 space-y-5 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-terracotta/10 flex items-center justify-center">
              <LockKey size={22} weight="duotone" className="text-terracotta" />
            </div>
            <h1 className="font-display font-black text-xl text-ink tracking-tight">Admin</h1>
          </div>
          <p className="text-sm text-ink-soft leading-relaxed font-sans">
            Area riservata. Inserisci il token di accesso.
          </p>
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            autoFocus
            placeholder="Token"
            data-testid="admin-token-input"
            className="h-11 rounded-xl"
          />
          {error && (
            <div className="text-sm text-terracotta flex items-center gap-1.5 font-sans" data-testid="admin-error">
              <Warning size={14} weight="bold" />
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={!token || loading}
            data-testid="admin-submit"
            className="w-full rounded-full bg-ink hover:bg-ink/90 text-white font-sans font-semibold h-11"
          >
            {loading ? "…" : "Entra"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-5 py-10 space-y-8">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold font-sans">Admin</p>
          <h1 className="font-display font-black text-3xl text-ink mt-1 tracking-tight" data-testid="admin-title">
            Statistiche utenti
          </h1>
        </div>
        <Button
          data-testid="admin-reload-stats"
          variant="outline"
          size="sm"
          disabled={loading}
          onClick={async () => {
            try { setLoading(true); await reload(token); } finally { setLoading(false); }
          }}
          className="rounded-full text-ink border-border"
        >
          {loading ? "Ricarico…" : "↻ Ricarica"}
        </Button>
      </div>
      {lastRefreshed && (
        <div className="text-[11px] font-sans text-ink-soft -mt-4" data-testid="admin-last-refreshed">
          Aggiornato alle {lastRefreshed.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })} · auto-refresh ogni 30s
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <StatCard testid="stat-external-users" label="Utenti esterni" value={data.external_users ?? 0}
                  sub={data.real_users ? `su ${data.real_users} reali` : ""} highlight />
        <StatCard testid="stat-internal-testers" label="Interni (io/famiglia)" value={data.internal_testers ?? 0} />
        <StatCard testid="stat-real-users" label="Tot. utenti reali" value={data.real_users}
                  sub={data.total_users ? `su ${data.total_users}` : ""} />
        <StatCard testid="stat-with-profile" label="Profilo (reali)" value={data.with_profile_real}
                  sub={data.real_users ? `${Math.round(100 * data.with_profile_real / data.real_users)}%` : ""} />
        <StatCard testid="stat-checklist-complete" label="Checklist 100% (est.)" value={data.checklist_complete}
                  sub={data.external_users ? `${Math.round(100 * data.checklist_complete / data.external_users)}%` : ""} />
      </div>

      {/* Cleanup action */}
      {data.test_users > 0 && (
        <div className="rounded-2xl border border-terracotta/30 bg-terracotta/5 p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-ink font-sans">
            Ci sono <strong>{data.test_users}</strong> account di test nel database (email a domini {"@ex.com/@x.com/@example.com"} o user_id non-Google).
          </div>
          <Button
            variant="ghost"
            size="sm"
            data-testid="admin-cleanup-btn"
            disabled={cleaning}
            onClick={cleanupTest}
            className="rounded-full text-terracotta hover:bg-terracotta/10 font-semibold"
          >
            {cleaning ? "Rimozione in corso…" : `Rimuovi ${data.test_users} account di test`}
          </Button>
        </div>
      )}
      {cleanupResult && (
        <div data-testid="admin-cleanup-result" className="rounded-xl bg-sage/10 border border-sage/30 p-3 text-sm text-ink font-sans">
          ✓ Rimossi <strong>{cleanupResult.deleted_users}</strong> account di test e i loro dati collegati.
        </div>
      )}

      {/* Plausible conversion funnel */}
      <PlausibleFunnelWidget token={token} />

      {/* Plausible per-phase engagement (unordered, one row per phase) */}
      <PlausibleEngagementWidget token={token} />

      {/* Link to per-device Plausible opt-out */}
      <div className="rounded-2xl border border-border bg-surface-card p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-ink-soft/10 flex items-center justify-center shrink-0">
            <EyeSlash size={18} weight="duotone" className="text-ink-soft" />
          </div>
          <div>
            <div className="font-sans font-semibold text-ink text-sm">Escludi questo dispositivo dal tracking Plausible</div>
            <div className="text-xs text-ink-soft font-sans mt-0.5">Ferma la contabilizzazione dei tuoi eventi su questo browser. Va ripetuto su ogni device/browser di test.</div>
          </div>
        </div>
        <Link
          to="/admin/plausible-optout"
          data-testid="admin-plausible-optout-link"
          className="rounded-full border border-border bg-surface hover:bg-surface-alt text-ink text-sm font-sans font-semibold px-4 py-2 transition-colors"
        >
          Gestisci
        </Link>
      </div>

      {/* Users table */}
      <div className="rounded-2xl border border-border bg-surface-card overflow-hidden">
        <table className="w-full text-sm font-sans" data-testid="admin-users-table">
          <thead className="bg-sand/20 text-ink-soft text-left">
            <tr>
              <th className="px-4 py-3 font-semibold">#</th>
              <th className="px-4 py-3 font-semibold">Anon ID</th>
              <th className="px-4 py-3 font-semibold">Email (mascherata)</th>
              <th className="px-4 py-3 font-semibold">Nome</th>
              <th className="px-4 py-3 font-semibold">Data registrazione</th>
              <th className="px-4 py-3 font-semibold">Profilo</th>
              <th className="px-4 py-3 font-semibold">Tipo</th>
              <th className="px-4 py-3 font-semibold">Interno</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u, i) => (
              <tr
                key={u.anon_id + i}
                className={`border-t border-border/60 ${u.is_test ? "opacity-60" : ""} ${u.is_internal_tester ? "bg-sand/10" : ""}`}
                data-testid={`admin-user-row-${u.anon_id}`}
                data-test-account={u.is_test ? "true" : "false"}
                data-internal-tester={u.is_internal_tester ? "true" : "false"}
              >
                <td className="px-4 py-2 text-ink-soft">{i + 1}</td>
                <td className="px-4 py-2 font-mono text-xs text-ink">{u.anon_id}</td>
                <td className="px-4 py-2 text-ink-soft/80 text-xs font-mono">{u.email_masked}</td>
                <td className="px-4 py-2 text-ink-soft">{u.name}</td>
                <td className="px-4 py-2 text-ink-soft">
                  {u.created_at ? new Date(u.created_at).toLocaleString("it-IT") : "—"}
                </td>
                <td className="px-4 py-2">
                  {u.has_profile
                    ? <span className="text-sage font-semibold">✓</span>
                    : <span className="text-ink-soft/50">—</span>}
                </td>
                <td className="px-4 py-2">
                  {u.is_test
                    ? <span className="text-[10px] uppercase tracking-widest font-sans font-bold text-terracotta bg-terracotta/10 px-2 py-0.5 rounded-full">test</span>
                    : u.is_internal_tester
                    ? <span className="text-[10px] uppercase tracking-widest font-sans font-bold text-ink bg-sand/40 px-2 py-0.5 rounded-full">interno</span>
                    : <span className="text-[10px] uppercase tracking-widest font-sans font-bold text-sage bg-sage/10 px-2 py-0.5 rounded-full">esterno</span>}
                </td>
                <td className="px-4 py-2">
                  {u.is_test ? (
                    <span className="text-ink-soft/40 text-xs">—</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleInternal(u.user_id, !u.is_internal_tester)}
                      data-testid={`admin-toggle-internal-${u.user_id}`}
                      aria-pressed={u.is_internal_tester}
                      className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${u.is_internal_tester ? "bg-terracotta" : "bg-border"}`}
                      title={u.is_internal_tester ? "Marcato come interno — clicca per rimuovere" : "Segna come utente interno (io, famiglia, amici)"}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${u.is_internal_tester ? "translate-x-5" : "translate-x-0.5"}`}
                      />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ testid, label, value, sub, highlight }) {
  return (
    <div
      className={`rounded-2xl border p-5 ${highlight ? "border-terracotta/40 bg-terracotta/5" : "border-border bg-surface-card"}`}
      data-testid={testid}
    >
      <div className="text-xs uppercase tracking-widest text-ink-soft font-sans font-bold">{label}</div>
      <div className="flex items-baseline gap-2 mt-2">
        <div className={`font-display font-black text-4xl ${highlight ? "text-terracotta" : "text-ink"}`}>{value}</div>
        {sub && <div className="text-sm text-ink-soft font-sans">{sub}</div>}
      </div>
    </div>
  );
}
